/**
 * AuthGrant React hooks — TanStack Query bindings.
 * STORY-1305 (Sprint 17 W1).
 */

import {
  createOneTimeGrant,
  createPermanentGrant,
  listActiveGrantsForGrantor,
  type AuthorizationGrant,
  type CreateOneTimeGrantInput,
  type CreatePermanentGrantInput,
} from '@/api/auth-grants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useActiveGrantsForGrantor(grantorPartnerId: string | null | undefined) {
  const query = useQuery({
    queryKey: ['auth-grants', 'active-for-grantor', grantorPartnerId],
    queryFn: async () => {
      if (!grantorPartnerId) return [];
      const res = await listActiveGrantsForGrantor(grantorPartnerId);
      return res.data;
    },
    enabled: !!grantorPartnerId,
  });

  return {
    grants: (query.data ?? []) as AuthorizationGrant[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCreatePermanentGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePermanentGrantInput) => createPermanentGrant(input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['auth-grants', 'active-for-grantor', vars.grantorPartnerId],
      });
    },
  });
}

export function useCreateOneTimeGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOneTimeGrantInput) => createOneTimeGrant(input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['auth-grants', 'active-for-grantor', vars.grantorPartnerId],
      });
    },
  });
}
